"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.convertListedVisualization = exports.convertVisualization = void 0;
var tslib_1 = require("tslib");
// (C) 2019-2022 GoodData Corporation
var sdk_model_1 = require("@gooddata/sdk-model");
var compact_1 = tslib_1.__importDefault(require("lodash/compact"));
var isEmpty_1 = tslib_1.__importDefault(require("lodash/isEmpty"));
var isNil_1 = tslib_1.__importDefault(require("lodash/isNil"));
var omit_1 = tslib_1.__importDefault(require("lodash/omit"));
var api_model_bear_1 = require("@gooddata/api-model-bear");
var ReferenceConverter_1 = require("./ReferenceConverter");
var PropertiesConverter_1 = require("./PropertiesConverter");
var ObjRefConverter_1 = require("./ObjRefConverter");
// we use more lenient uri "detection" here because the one in bear-client makes some legacy data fail
// as the objId is not always just a number
var isUriLike = function (value) { return /\/gdc\/md\/\S+\/obj\/\S+/.test(value); };
var convertAttributeElements = function (items) {
    if (!items.length) {
        // in case of empty filter assume that it is meant to be an URI-based one as these are much more common on bear
        return { uris: [] };
    }
    // we assume that all the items either use uris, or values, not both, since there is no way of representing the mixed variant
    var first = items[0];
    return isUriLike(first) ? { uris: items } : { values: items };
};
var convertMeasureValueFilter = function (filter) {
    return {
        measureValueFilter: {
            condition: filter.measureValueFilter.condition,
            measure: ObjRefConverter_1.fromScopedBearRef(filter.measureValueFilter.measure, "measure"),
        },
    };
};
var convertRankingFilter = function (filter) {
    var _a = filter.rankingFilter, measures = _a.measures, operator = _a.operator, value = _a.value, attributes = _a.attributes;
    return {
        rankingFilter: {
            measure: ObjRefConverter_1.fromScopedBearRef(measures[0], "measure"),
            operator: operator,
            value: value,
            attributes: attributes,
        },
    };
};
var convertFilter = function (filter) {
    if (api_model_bear_1.GdcVisualizationObject.isMeasureValueFilter(filter)) {
        return convertMeasureValueFilter(filter);
    }
    else if (api_model_bear_1.GdcVisualizationObject.isRankingFilter(filter)) {
        return convertRankingFilter(filter);
    }
    else {
        return convertMeasureFilter(filter);
    }
};
var convertMeasureFilter = function (filter) {
    if (api_model_bear_1.GdcVisualizationObject.isAttributeFilter(filter)) {
        if (api_model_bear_1.GdcVisualizationObject.isPositiveAttributeFilter(filter)) {
            return {
                positiveAttributeFilter: {
                    displayForm: ObjRefConverter_1.fromBearRef(filter.positiveAttributeFilter.displayForm, "displayForm"),
                    in: convertAttributeElements(filter.positiveAttributeFilter.in),
                },
            };
        }
        return {
            negativeAttributeFilter: {
                displayForm: ObjRefConverter_1.fromBearRef(filter.negativeAttributeFilter.displayForm, "displayForm"),
                notIn: convertAttributeElements(filter.negativeAttributeFilter.notIn),
            },
        };
    }
    else {
        if (api_model_bear_1.GdcVisualizationObject.isAbsoluteDateFilter(filter)) {
            return {
                absoluteDateFilter: {
                    dataSet: ObjRefConverter_1.fromBearRef(filter.absoluteDateFilter.dataSet, "dataSet"),
                    from: filter.absoluteDateFilter.from || "",
                    to: filter.absoluteDateFilter.to || "",
                },
            };
        }
        // check for all-time filters with missing bounds (even one missing bound suggests an all time filter)
        // we cannot remove them, as they do make sense in some rare legacy contexts
        if (isNil_1.default(filter.relativeDateFilter.from) || isNil_1.default(filter.relativeDateFilter.to)) {
            // eslint-disable-next-line no-console
            console.warn("RelativeDateFilter without 'from' or 'to' field encountered." +
                "This can make sense in some legacy contexts (e.g. PoP measures with All time global filter), but generally, this indicates an error." +
                "Please check the visualization object data to make sure the relativeDateFilter data is what you expected.");
        }
        return {
            relativeDateFilter: tslib_1.__assign(tslib_1.__assign({}, filter.relativeDateFilter), { granularity: filter.relativeDateFilter.granularity, from: filter.relativeDateFilter.from, to: filter.relativeDateFilter.to }),
        };
    }
};
var convertMeasureDefinition = function (definition) {
    if (api_model_bear_1.GdcVisualizationObject.isArithmeticMeasureDefinition(definition)) {
        return definition;
    }
    if (api_model_bear_1.GdcVisualizationObject.isPopMeasureDefinition(definition)) {
        return definition;
    }
    if (api_model_bear_1.GdcVisualizationObject.isPreviousPeriodMeasureDefinition(definition)) {
        return definition;
    }
    var filters = definition.measureDefinition.filters;
    return {
        measureDefinition: tslib_1.__assign(tslib_1.__assign({}, definition.measureDefinition), { filters: filters ? compact_1.default(filters.map(convertMeasureFilter)) : [] }),
    };
};
var convertMeasure = function (measure) {
    var definition = measure.measure.definition;
    return {
        measure: tslib_1.__assign(tslib_1.__assign({}, measure.measure), { definition: convertMeasureDefinition(definition) }),
    };
};
var convertAttribute = function (attribute) {
    return {
        attribute: tslib_1.__assign(tslib_1.__assign({}, attribute.visualizationAttribute), { displayForm: ObjRefConverter_1.fromBearRef(attribute.visualizationAttribute.displayForm, "displayForm") }),
    };
};
var convertBucketItem = function (bucketItem) {
    return api_model_bear_1.GdcVisualizationObject.isMeasure(bucketItem)
        ? convertMeasure(bucketItem)
        : convertAttribute(bucketItem);
};
var convertBucket = function (bucket) {
    return {
        items: bucket.items.map(convertBucketItem),
        localIdentifier: bucket.localIdentifier,
        totals: bucket.totals,
    };
};
var resolveReferences = function (mdObject) {
    var content = mdObject.content;
    if (!content) {
        return mdObject;
    }
    var properties = content.properties;
    if (!properties) {
        return mdObject;
    }
    var _a = ReferenceConverter_1.convertReferencesToUris({
        properties: PropertiesConverter_1.deserializeProperties(properties),
        references: content.references || {},
    }), convertedProperties = _a.properties, convertedReferences = _a.references;
    // set the new properties and references
    var referencesProp = isEmpty_1.default(convertedReferences) ? undefined : { references: convertedReferences };
    return tslib_1.__assign(tslib_1.__assign({}, mdObject), { content: tslib_1.__assign(tslib_1.__assign(tslib_1.__assign({}, omit_1.default(mdObject.content, "references")), { properties: PropertiesConverter_1.serializeProperties(convertedProperties) }), referencesProp) });
};
/**
 *
 * @internal
 */
exports.convertVisualization = function (visualization, visualizationClassUri, userMap) {
    var _a, _b, _c;
    var withResolvedReferences = resolveReferences(visualization.visualizationObject);
    var content = withResolvedReferences.content, meta = withResolvedReferences.meta;
    var parsedProperties = PropertiesConverter_1.deserializeProperties(content.properties);
    return {
        insight: {
            buckets: content.buckets.map(convertBucket),
            filters: content.filters ? compact_1.default(content.filters.map(convertFilter)) : [],
            ref: sdk_model_1.uriRef(meta.uri),
            // we assume that identifier is always defined for visualizations
            identifier: meta.identifier,
            properties: parsedProperties,
            sorts: parsedProperties.sortItems || [],
            title: meta.title,
            uri: meta.uri,
            visualizationUrl: visualizationClassUri,
            created: meta.created,
            createdBy: meta.author ? userMap === null || userMap === void 0 ? void 0 : userMap.get(meta.author) : undefined,
            updated: meta.updated,
            updatedBy: meta.contributor ? userMap === null || userMap === void 0 ? void 0 : userMap.get(meta.contributor) : undefined,
            isLocked: meta.locked,
            tags: (_b = (_a = meta.tags) === null || _a === void 0 ? void 0 : _a.split(" ").filter(Boolean)) !== null && _b !== void 0 ? _b : [],
            summary: (_c = meta.summary) !== null && _c !== void 0 ? _c : "",
        },
    };
};
exports.convertListedVisualization = function (visualizationLink) {
    var ref = sdk_model_1.uriRef(visualizationLink.link);
    return {
        insight: {
            identifier: visualizationLink.identifier || "",
            title: visualizationLink.title || "",
            uri: visualizationLink.link,
            ref: ref,
            properties: [],
            sorts: [],
            visualizationUrl: "",
            buckets: [],
            filters: [],
            tags: [],
            created: visualizationLink.created,
            updated: visualizationLink.updated,
        },
    };
};
//# sourceMappingURL=VisualizationConverter.js.map